/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import type {
  VideoPlayerUiState,
  PlaybackStatePayload,
  AiringInfoPayload,
  TrickPlayStatePayload,
  TrickPlayEligibilityPayload,
  PlayablePayload,
  AudioTracksPayload,
  ActionKernelTypesPayload,
  MetadataStatePayload,
  SessionStatePayload,
  StandalonePlaybackStatePayload
} from "./videoPlayerUi.types";
import { INITIAL_STATE } from "./videoPlayerUi.types";
import type { Direction } from "../../model/videoplayer/trickplay";
import { PlayerState } from "../../model/videoplayer/enums";

const videoPlayerUiSlice = createSlice({
  name: "videoPlayerUi",
  initialState: INITIAL_STATE,
  reducers: {
    playbackStateUpdated(
      state: VideoPlayerUiState,
      action: PayloadAction<PlaybackStatePayload>
    ): void {
      state.currentTime = action.payload.currentTime;
      state.duration = action.payload.duration;
      state.playerState = action.payload.playerState;
      // vpc.getSeekRange() builds a fresh object on every call, so assigning it
      // unconditionally made Immer produce a new root state on every 1 s poll
      // tick even when the range had not moved — re-rendering every selector
      // subscriber in the player tree. Compare by value instead.
      const nextSeekRange = action.payload.seekRange;
      if (
        state.seekRange.start !== nextSeekRange.start ||
        state.seekRange.end !== nextSeekRange.end ||
        state.seekRange.duration !== nextSeekRange.duration
      ) {
        state.seekRange = nextSeekRange;
      }
      state.isStartOverPlaying = action.payload.isStartOverPlaying;
      state.isPositionSettled = action.payload.isPositionSettled;
    },
    airingInfoUpdated(
      state: VideoPlayerUiState,
      action: PayloadAction<AiringInfoPayload>
    ): void {
      state.startTimeLabel = action.payload.startTimeLabel;
      state.endTimeLabel = action.payload.endTimeLabel;
      state.airingStartEpoch = action.payload.airingStartEpoch;
      state.airingEndEpoch = action.payload.airingEndEpoch;
      state.parentalControlsRating = action.payload.parentalControlsRating;
      state.programDetails = action.payload.programDetails;
    },
    trickPlayStateUpdated(
      state: VideoPlayerUiState,
      action: PayloadAction<TrickPlayStatePayload>
    ): void {
      state.isTrickPlayActive = action.payload.isTrickPlayActive;
      state.trickPlayMode = action.payload.trickPlayMode;
      state.targetPosition = action.payload.targetPosition;
      state.seekDirection = action.payload.seekDirection;
      state.currentSpeedLevel = action.payload.currentSpeedLevel;
      state.seekEntryPoint = action.payload.seekEntryPoint;
    },
    trickPlayEligibilityUpdated(
      state: VideoPlayerUiState,
      action: PayloadAction<TrickPlayEligibilityPayload>
    ): void {
      state.trickPlayEligibility = action.payload.eligibility;
      state.trickPlayRestrictionEnabled = action.payload.restrictionEnabled;
    },
    speedTriggerMethodUpdated(
      state: VideoPlayerUiState,
      action: PayloadAction<boolean>
    ): void {
      state.speedTriggeredByLongPress = action.payload;
    },
    skipOccurred(
      state: VideoPlayerUiState,
      action: PayloadAction<Direction>
    ): void {
      state.skipCounter = state.skipCounter + 1;
      state.seekDirection = action.payload;
    },
    playableUpdated(
      state: VideoPlayerUiState,
      action: PayloadAction<PlayablePayload>
    ): void {
      state.playable = action.payload.playable;
    },
    audioTracksUpdated(
      state: VideoPlayerUiState,
      action: PayloadAction<AudioTracksPayload>
    ): void {
      state.audioTracks = action.payload.audioTracks;
      state.selectedAudioTrackId = action.payload.selectedAudioTrackId;
    },
    mediaTracksReadyUpdated(
      state: VideoPlayerUiState,
      action: PayloadAction<boolean>
    ): void {
      state.areMediaTracksReady = action.payload;
    },
    setWasPiGEnteredFromMiniClick(
      state: VideoPlayerUiState,
      action: PayloadAction<boolean>
    ): void {
      state.wasPiGEnteredFromMiniClick = action.payload;
    },
    actionKernelTypesUpdated(
      state: VideoPlayerUiState,
      action: PayloadAction<ActionKernelTypesPayload>
    ): void {
      state.actionKernelTypes = action.payload.actionKernelTypes;
      state.onePassActionInfo = action.payload.onePassActionInfo;
      state.onePassModifyInfo = action.payload.onePassModifyInfo;
      state.modifyRecordingCollectionId =
        action.payload.modifyRecordingCollectionId;
    },
    metadataStateUpdated(
      state: VideoPlayerUiState,
      action: PayloadAction<MetadataStatePayload>
    ): void {
      state.metadataState = action.payload.metadataState;
    },
    sessionStateUpdated(
      state: VideoPlayerUiState,
      action: PayloadAction<SessionStatePayload>
    ): void {
      state.sessionState = action.payload.sessionState;
    },
    standalonePlaybackStateUpdated(
      state: VideoPlayerUiState,
      action: PayloadAction<StandalonePlaybackStatePayload>
    ): void {
      if (action.payload.state === PlayerState.PLAYING) {
        state.isStandalonePlaybackActive = true;
        state.hasStandalonePlaybackEnded = false;
        state.standalonePlaybackError = null;
      } else if (action.payload.state === PlayerState.ENDED) {
        state.isStandalonePlaybackActive = false;
        state.hasStandalonePlaybackEnded = true;
      } else if (action.payload.state === PlayerState.ERROR) {
        // A failed source never raises `ended`, so a failure is reported as an
        // end too — otherwise the View would wait forever and strand the alert
        // on screen.
        state.isStandalonePlaybackActive = false;
        state.hasStandalonePlaybackEnded = true;
        const message = action.payload.errorMessage;
        state.standalonePlaybackError =
          message !== undefined ? message : "Standalone playback error";
      }
    },
    standalonePlaybackEndHandled(state: VideoPlayerUiState): void {
      state.hasStandalonePlaybackEnded = false;
      state.standalonePlaybackError = null;
    },
    exitToPreviousScreenRequested(state: VideoPlayerUiState): void {
      state.shouldExitToPreviousScreen = true;
    },
    exitToPreviousScreenHandled(state: VideoPlayerUiState): void {
      state.shouldExitToPreviousScreen = false;
    },
    videoPlayerUiReset(state: VideoPlayerUiState): VideoPlayerUiState {
      // Preserve the one-shot PiG-from-mini-click flag so the PiG↔PiP bridge
      // survives the reset that fires on player unmount during navigation.
      return {
        ...INITIAL_STATE,
        wasPiGEnteredFromMiniClick: state.wasPiGEnteredFromMiniClick
      };
    }
  }
});

export const {
  playbackStateUpdated,
  airingInfoUpdated,
  trickPlayStateUpdated,
  trickPlayEligibilityUpdated,
  speedTriggerMethodUpdated,
  playableUpdated,
  skipOccurred,
  audioTracksUpdated,
  mediaTracksReadyUpdated,
  setWasPiGEnteredFromMiniClick,
  actionKernelTypesUpdated,
  metadataStateUpdated,
  sessionStateUpdated,
  standalonePlaybackStateUpdated,
  standalonePlaybackEndHandled,
  exitToPreviousScreenRequested,
  exitToPreviousScreenHandled,
  videoPlayerUiReset
} = videoPlayerUiSlice.actions;

export const videoPlayerUiReducer = videoPlayerUiSlice.reducer;
